import {
  useEffect,
  useMemo,
  useState
} from 'react';

// ALLOW_PDF_IMAGES_V1

import {
  fetchFilesForSubmission,
  finalizeMultiFileSubmission,
  getSubmissionFileAccept,
  getSubmissionFileFormatLabel,
  prepareMultiFileDraft,
  uploadMultiFileSlot,
  validateConfiguredSubmissionFile
} from '../../../utils/assignmentSubmissionFiles';


export default function MultiFileSubmissionControls({
  submission,
  slots,
  courseId,
  status,
  ensureSubmission,
  buildStoredFileName,
  getFinalizeData,
  onSubmitted,
  allowPdfImages = false,
  allowSubmittedEditing = false,
  skipFileValidation = false,
  children
}) {
  const [
    existingFiles,
    setExistingFiles
  ] =
    useState([]);

  const [
    selectedFiles,
    setSelectedFiles
  ] =
    useState({});

  const [
    loading,
    setLoading
  ] =
    useState(false);

  const [
    submitting,
    setSubmitting
  ] =
    useState(false);

  const [
    currentUploadLabel,
    setCurrentUploadLabel
  ] =
    useState('');

  const [
    error,
    setError
  ] =
    useState('');

  const [
    success,
    setSuccess
  ] =
    useState('');


  const submissionId =
    submission?.id ||
    '';

  const sourceSubmissionId =
    submission
      ?.file_source_submission_id ||
    '';


  const orderedSlots =
    useMemo(
      () =>
        (
          Array.isArray(slots)
            ? slots
            : []
        )
          .slice()
          .sort(
            (a, b) =>
              Number(
                a.display_order ||
                0
              ) -
                Number(
                  b.display_order ||
                  0
                ) ||
              String(
                a.id
              ).localeCompare(
                String(
                  b.id
                )
              )
          ),
      [slots]
    );


  useEffect(() => {
    let cancelled =
      false;

    setSelectedFiles({});
    setSuccess('');

    async function load() {
      if (!submissionId) {
        setExistingFiles([]);
        return;
      }

      setLoading(true);

      try {
        const rows =
          await fetchFilesForSubmission({
            id:
              submissionId,

            file_source_submission_id:
              sourceSubmissionId ||
              null
          });

        if (!cancelled) {
          setExistingFiles(
            rows
          );
        }

      } catch (loadError) {
        console.error(
          'Loading multi-file submission failed:',
          loadError
        );

        if (!cancelled) {
          setError(
            loadError?.message ||
            'Could not load submission files.'
          );
        }

      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled =
        true;
    };
  }, [
    submissionId,
    sourceSubmissionId
  ]);


  const existingBySlot =
    useMemo(
      () =>
        new Map(
          existingFiles
            .filter(
              row =>
                row.slot_id
            )
            .map(
              row => [
                String(
                  row.slot_id
                ),
                row
              ]
            )
        ),
      [existingFiles]
    );


  async function chooseFile(
    slot,
    file
  ) {
    if (!file) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      if (!skipFileValidation) {
        await validateConfiguredSubmissionFile(
          file,
          {
            allowed_pdf:
              slot.allow_pdf,

            allowed_docx:
              slot.allow_docx,

            allow_pdf_images:
              allowPdfImages
          }
        );
      }

      setSelectedFiles(
        previous => ({
          ...previous,

          [slot.id]:
            file
        })
      );

    } catch (chooseError) {
      setSelectedFiles(
        previous => {
          const next = {
            ...previous
          };

          delete next[
            slot.id
          ];

          return next;
        }
      );

      setError(
        chooseError?.message ||
        'That file cannot be submitted.'
      );
    }
  }


  async function submit() {
    setError('');
    setSuccess('');

    if (!orderedSlots.length) {
      setError(
        'This assignment is configured for multiple files, but no submission files have been defined.'
      );

      return;
    }

    const missingRequired =
      orderedSlots.find(
        slot =>
          slot.required ===
            true &&
          !selectedFiles[
            slot.id
          ] &&
          !existingBySlot.has(
            String(
              slot.id
            )
          )
      );

    if (missingRequired) {
      setError(
        'Required submission file missing: ' +
        missingRequired.label +
        '.'
      );

      return;
    }

    let extraData = {};

    try {
      extraData =
        getFinalizeData
          ? (
              await getFinalizeData()
            ) || {}
          : {};

    } catch (validationError) {
      setError(
        validationError
          ?.message ||
        'The submission is incomplete.'
      );

      return;
    }

    setSubmitting(true);

    try {
      let currentSubmission =
        await ensureSubmission();

      if (!currentSubmission?.id) {
        throw new Error(
          'The assignment submission could not be created.'
        );
      }

      const prepared =
        await prepareMultiFileDraft({
          submission:
            currentSubmission,

          slots:
            orderedSlots
        });

      currentSubmission =
        prepared.submission;

      let workingFiles =
        prepared.files;

      for (
        const slot
        of orderedSlots
      ) {
        const file =
          selectedFiles[
            slot.id
          ];

        if (!file) {
          continue;
        }

        setCurrentUploadLabel(
          slot.label
        );

        const existing =
          workingFiles.find(
            row =>
              String(
                row.slot_id
              ) ===
              String(
                slot.id
              )
          ) || null;

        const storedFileName =
          buildStoredFileName(
            slot,
            file
          );

        const saved =
          await uploadMultiFileSlot({
            submission:
              currentSubmission,

            slot,

            file,

            courseId,

            storedFileName,

            existingFile:
              existing,

            allowPdfImages,
            skipValidation:
              skipFileValidation
          });

        workingFiles =
          [
            ...workingFiles.filter(
              row =>
                String(
                  row.slot_id
                ) !==
                String(
                  slot.id
                )
            ),

            saved
          ].filter(Boolean);
      }

      const refreshedFiles =
        await fetchFilesForSubmission({
          ...currentSubmission,

          file_source_submission_id:
            currentSubmission.id
        });

      setExistingFiles(
        refreshedFiles
      );

      const finalSubmission =
        await finalizeMultiFileSubmission({
          submission:
            currentSubmission,

          slots:
            orderedSlots,

          extraData
        });

      setSelectedFiles({});

      setSuccess(
        'Submitted successfully.'
      );

      if (onSubmitted) {
        await onSubmitted(
          finalSubmission
        );
      }

    } catch (submitError) {
      console.error(
        'Multi-file submission failed:',
        submitError
      );

      setError(
        submitError?.message ||
        'The assignment could not be submitted.'
      );

    } finally {
      setSubmitting(false);
      setCurrentUploadLabel('');
    }
  }


  if (loading) {
    return (
      <div
        style={
          styles.muted
        }
      >
        Loading submission files...
      </div>
    );
  }


  if (
    submission?.submitted &&
    !allowSubmittedEditing
  ) {
    return (
      <div>
        <div
          style={
            styles.submitted
          }
        >
          ✓ Submitted
        </div>

        <div
          style={
            styles.fileList
          }
        >
          {existingFiles.map(
            file => (
              <div
                key={
                  file.id
                }
                style={
                  styles.submittedFile
                }
              >
                <strong>
                  {
                    file.slot_label ||
                    'Submission file'
                  }
                </strong>

                <span
                  style={
                    styles.fileName
                  }
                >
                  {
                    file
                      .original_file_name ||
                    file.file_name
                  }
                </span>
              </div>
            )
          )}
        </div>
      </div>
    );
  }


  if (status !== 'open') {
    return (
      <div
        style={
          styles.closed
        }
      >
        {status === 'not_open'
          ? 'Submissions not yet open.'
          : status === 'closed'
            ? 'Submission window has closed.'
            : 'No submission dates configured.'}
      </div>
    );
  }


  return (
    <div>
      {error && (
        <div
          style={
            styles.error
          }
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={
            styles.success
          }
        >
          {success}
        </div>
      )}

      {!orderedSlots.length ? (
        <div
          style={
            styles.error
          }
        >
          No submission files have been configured.
        </div>

      ) : (
        <div
          style={
            styles.slotList
          }
        >
          {orderedSlots.map(
            slot => {
              const existing =
                existingBySlot.get(
                  String(
                    slot.id
                  )
                );

              const selected =
                selectedFiles[
                  slot.id
                ];

              return (
                <div
                  key={
                    slot.id
                  }
                  style={
                    styles.slot
                  }
                >
                  <div
                    style={
                      styles.slotHeading
                    }
                  >
                    <strong>
                      {slot.label}
                    </strong>

                    {slot.required && (
                      <span
                        style={
                          styles.required
                        }
                      >
                        Required
                      </span>
                    )}
                  </div>

                  <div
                    style={
                      styles.format
                    }
                  >
                    Accepted:{' '}
                    {
                      getSubmissionFileFormatLabel({
                        allowed_pdf:
                          slot.allow_pdf,

                        allowed_docx:
                          slot.allow_docx
                      })
                    }
                  </div>

                  {existing && (
                    <div
                      style={
                        styles.existing
                      }
                    >
                      Current file:{' '}
                      <strong>
                        {
                          existing
                            .original_file_name ||
                          existing
                            .file_name
                        }
                      </strong>
                    </div>
                  )}

                  <label
                    style={
                      styles.choose
                    }
                  >
                    <input
                      type="file"
                      accept={
                        skipFileValidation
                          ? '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                          : getSubmissionFileAccept({
                              allowed_pdf:
                                slot.allow_pdf,

                              allowed_docx:
                                slot.allow_docx
                            })
                      }
                      style={{
                        display:
                          'none'
                      }}
                      disabled={
                        submitting
                      }
                      onChange={
                        event =>
                          chooseFile(
                            slot,
                            event
                              .target
                              .files
                              ?.[0]
                          )
                      }
                    />

                    <span
                      style={
                        styles.chooseButton
                      }
                    >
                      {existing
                        ? 'Choose replacement'
                        : 'Choose file'}
                    </span>
                  </label>

                  {selected && (
                    <div
                      style={
                        styles.selected
                      }
                    >
                      Selected:{' '}
                      <strong>
                        {
                          selected.name
                        }
                      </strong>
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      )}

      {children}

      <button
        type="button"
        style={
          styles.submit
        }
        disabled={
          submitting ||
          !orderedSlots.length
        }
        onClick={
          submit
        }
      >
        {submitting
          ? (
              currentUploadLabel
                ? (
                    'Uploading ' +
                    currentUploadLabel +
                    '...'
                  )
                : 'Submitting...'
            )
          : 'Submit'}
      </button>
    </div>
  );
}


const styles = {
  muted: {
    color:
      '#627d98',
    fontSize:
      '13px'
  },

  slotList: {
    display:
      'grid',
    gap:
      '11px'
  },

  slot: {
    padding:
      '12px',
    border:
      '1px solid #d9e2ec',
    borderRadius:
      '8px',
    background:
      'white'
  },

  slotHeading: {
    display:
      'flex',
    alignItems:
      'center',
    gap:
      '8px',
    marginBottom:
      '4px',
    fontSize:
      '13px',
    color:
      '#243b53'
  },

  required: {
    borderRadius:
      '999px',
    background:
      '#edf2f7',
    color:
      '#627d98',
    padding:
      '2px 7px',
    fontSize:
      '10px',
    fontWeight:
      800
  },

  format: {
    color:
      '#829ab1',
    fontSize:
      '11px',
    marginBottom:
      '8px'
  },

  existing: {
    color:
      '#486581',
    fontSize:
      '12px',
    marginBottom:
      '8px'
  },

  choose: {
    display:
      'inline-block',
    cursor:
      'pointer'
  },

  chooseButton: {
    display:
      'inline-block',
    borderRadius:
      '6px',
    padding:
      '7px 10px',
    background:
      '#215b7d',
    color:
      'white',
    fontSize:
      '12px',
    fontWeight:
      700
  },

  selected: {
    marginTop:
      '7px',
    color:
      '#486581',
    fontSize:
      '12px'
  },

  submit: {
    marginTop:
      '12px',
    border:
      'none',
    borderRadius:
      '7px',
    padding:
      '8px 14px',
    background:
      '#215b7d',
    color:
      'white',
    fontWeight:
      800,
    cursor:
      'pointer'
  },

  error: {
    marginBottom:
      '10px',
    padding:
      '9px 10px',
    border:
      '1px solid #e8bcbc',
    borderRadius:
      '7px',
    background:
      '#faeaea',
    color:
      '#a12a2a',
    fontSize:
      '12px'
  },

  success: {
    marginBottom:
      '10px',
    color:
      '#216e45',
    fontSize:
      '12px',
    fontWeight:
      700
  },

  submitted: {
    color:
      '#216e45',
    fontWeight:
      800,
    marginBottom:
      '10px'
  },

  fileList: {
    display:
      'grid',
    gap:
      '6px'
  },

  submittedFile: {
    display:
      'flex',
    justifyContent:
      'space-between',
    gap:
      '12px',
    padding:
      '8px 10px',
    border:
      '1px solid #e1e7ec',
    borderRadius:
      '7px',
    background:
      'white',
    fontSize:
      '12px'
  },

  fileName: {
    color:
      '#627d98',
    textAlign:
      'right'
  },

  closed: {
    color:
      '#7a5310',
    fontSize:
      '12px'
  }
};
